class FreeProductsProgress extends HTMLElement {
  constructor() {
    super();
    this.cartTotal = parseInt(this.getAttribute('data-cart-total')) || 0;
    this.threshold1 = parseInt(this.getAttribute('data-threshold-1')) || 0;
    this.threshold2 = parseInt(this.getAttribute('data-threshold-2')) || 0;
    this.threshold3 = parseInt(this.getAttribute('data-threshold-3')) || 0;
    this.currencySymbol = this.getAttribute('data-currency-symbol') || '$';
    this.currencyCode = this.getAttribute('data-currency-code') || 'USD';
    this.variant1 = parseInt(this.getAttribute('data-variant-1')) || null;
    this.variant2 = parseInt(this.getAttribute('data-variant-2')) || null;
    this.variant3 = parseInt(this.getAttribute('data-variant-3')) || null;
    this.latestCart = null;
    this._addedGiftVariantIds = new Set();
    this._isAddingGift = false;
    this._isRemovingGift = false;
    this._pendingCartUpdate = null;
    this._debounceTimeout = null;
    this._isValidatingCheckout = false;
    this._checkoutValidated = false; // Flag to allow validated form submissions
    
    // Global checkout lock to prevent all cart operations during checkout
    if (!window._checkoutLock) {
      window._checkoutLock = {
        isLocked: false,
        lockedBy: null
      };
    }
    
    this.init();
  }

  init() {
    this.updateProgress();
    this.setupEventListeners();
    this.setupCheckoutInterception();
    // Evaluate immediately on load in case thresholds are already met
    // Fetch latest cart to ensure we have items for duplicate checks
    this.fetchUpdatedCartData();
  }

  setupEventListeners() {
    // Debounce cart updates to prevent multiple rapid calls
    const debouncedFetch = () => {
      // Don't process cart updates if checkout is being validated
      if (window._checkoutLock && window._checkoutLock.isLocked) {
        return;
      }
      
      if (this._debounceTimeout) {
        clearTimeout(this._debounceTimeout);
      }
      this._debounceTimeout = setTimeout(() => {
        // Double-check lock before executing
        if (window._checkoutLock && window._checkoutLock.isLocked) {
          return;
        }
        this.fetchUpdatedCartData();
      }, 300); // 300ms debounce
    };

    document.addEventListener('cart:updated', debouncedFetch);
    document.addEventListener('cart:drawer:updated', debouncedFetch);
    // Some parts of the theme dispatch 'cart-updated' (hyphen)
    document.addEventListener('cart-updated', debouncedFetch);
  }

  setupCheckoutInterception() {
    // Intercept checkout button clicks to ensure cart is in correct state
    // Use event delegation to handle dynamically loaded buttons
    const handleCheckoutClick = async (e) => {
      const checkoutButton = e.target.closest('#CheckOut');
      if (!checkoutButton) return;
      
      const cartForm = document.getElementById('cart');
      if (!cartForm) return;
      
      // Prevent re-entry if already validating
      if (this._isValidatingCheckout) {
        return;
      }
      
      // Prevent default form submission
      e.preventDefault();
      e.stopPropagation();
      
      this._isValidatingCheckout = true;
      
      // Disable button to prevent double-clicks
      checkoutButton.disabled = true;
      const originalText = checkoutButton.querySelector('.button__text')?.textContent || 'Secure Checkout';
      if (checkoutButton.querySelector('.button__text')) {
        checkoutButton.querySelector('.button__text').textContent = 'Processing...';
      }
      
      try {
        // LOCK: Prevent all cart operations during checkout validation
        window._checkoutLock.isLocked = true;
        window._checkoutLock.lockedBy = 'checkout-validation';
        
        // Cancel any pending debounced operations
        if (this._debounceTimeout) {
          clearTimeout(this._debounceTimeout);
          this._debounceTimeout = null;
        }
        
        // Wait for any pending cart operations to complete (optimized - shorter timeout)
        await this.waitForCartOperations();
        
        // Smart validation: only do second pass if items were removed
        let itemsRemoved = await this.validateAndFixCartState();
        
        // Only do second pass if we removed items (to catch any edge cases)
        if (itemsRemoved > 0) {
          // Short wait for server to process removals
          await new Promise(resolve => setTimeout(resolve, 150));
          // Second validation pass
          await this.validateAndFixCartState();
        }
        
        // Minimal final wait
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // UNLOCK: Allow operations again
        window._checkoutLock.isLocked = false;
        window._checkoutLock.lockedBy = null;
        
        // Mark that validation is complete - this allows form submission to proceed
        this._isValidatingCheckout = false;
        this._checkoutValidated = true; // Additional flag to bypass interceptor
        
        // Re-enable button (form submission will handle the rest)
        checkoutButton.disabled = false;
        if (checkoutButton.querySelector('.button__text')) {
          checkoutButton.querySelector('.button__text').textContent = originalText;
        }
        
        // Redirect directly to checkout after validation
        // This ensures we get the correct checkout screen
        window.location.href = '/checkout';
      } catch (error) {
        console.error('Error during checkout validation:', error);
        
        // UNLOCK on error
        window._checkoutLock.isLocked = false;
        window._checkoutLock.lockedBy = null;
        
        // Re-enable button on error
        checkoutButton.disabled = false;
        if (checkoutButton.querySelector('.button__text')) {
          checkoutButton.querySelector('.button__text').textContent = originalText;
        }
        this._isValidatingCheckout = false;
        this._checkoutValidated = true; // Mark as validated even on error
        
        // Re-enable button
        checkoutButton.disabled = false;
        if (checkoutButton.querySelector('.button__text')) {
          checkoutButton.querySelector('.button__text').textContent = originalText;
        }
        
        // Redirect directly to checkout (fail gracefully)
        window.location.href = '/checkout';
      }
    };
    
    // Use event delegation on document to catch clicks even if button is dynamically loaded
    document.addEventListener('click', handleCheckoutClick, true);
    
    // Also intercept form submission as a fallback (for cases where form is submitted without button click)
    const setupFormListener = () => {
      const cartForm = document.getElementById('cart');
      if (cartForm && !cartForm.dataset.checkoutListenerAdded) {
        cartForm.dataset.checkoutListenerAdded = 'true';
        cartForm.addEventListener('submit', async (e) => {
          // Skip if we're already validating (prevents infinite loop)
          if (this._isValidatingCheckout) {
            return; // Let the validation proceed
          }
          
          // Skip if checkout was already validated (allows our programmatic submit to proceed)
          if (this._checkoutValidated) {
            this._checkoutValidated = false; // Reset for next time
            return; // Allow form submission to proceed normally
          }
          
          // Only intercept if this is a checkout submission (not programmatic)
          const submitter = e.submitter;
          if (submitter && (submitter.id === 'CheckOut' || submitter.name === 'checkout')) {
            // If button is already disabled, it means click handler is processing
            if (submitter.disabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            
            // Otherwise, this is a direct form submission - validate it
            e.preventDefault();
            e.stopPropagation();
            
            this._isValidatingCheckout = true;
            submitter.disabled = true;
            const originalText = submitter.querySelector('.button__text')?.textContent || 'Secure Checkout';
            if (submitter.querySelector('.button__text')) {
              submitter.querySelector('.button__text').textContent = 'Processing...';
            }
            
            try {
              // LOCK: Prevent all cart operations during checkout validation
              window._checkoutLock.isLocked = true;
              window._checkoutLock.lockedBy = 'checkout-validation';
              
              // Cancel any pending debounced operations
              if (this._debounceTimeout) {
                clearTimeout(this._debounceTimeout);
                this._debounceTimeout = null;
              }
              
              await this.waitForCartOperations();
              
              // Smart validation: only do second pass if items were removed
              let itemsRemoved = await this.validateAndFixCartState();
              
              // Only do second pass if we removed items
              if (itemsRemoved > 0) {
                await new Promise(resolve => setTimeout(resolve, 150));
                await this.validateAndFixCartState();
              }
              
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // UNLOCK
              window._checkoutLock.isLocked = false;
              window._checkoutLock.lockedBy = null;
              
              this._isValidatingCheckout = false;
              this._checkoutValidated = true;
              
              // Re-enable button
              submitter.disabled = false;
              if (submitter.querySelector('.button__text')) {
                submitter.querySelector('.button__text').textContent = originalText;
              }
              
              // Redirect directly to checkout
              window.location.href = '/checkout';
            } catch (error) {
              console.error('Error during checkout validation:', error);
              
              // UNLOCK on error
              window._checkoutLock.isLocked = false;
              window._checkoutLock.lockedBy = null;
              
              submitter.disabled = false;
              if (submitter.querySelector('.button__text')) {
                submitter.querySelector('.button__text').textContent = originalText;
              }
              this._isValidatingCheckout = false;
              this._checkoutValidated = true;
              
              // Redirect directly to checkout
              window.location.href = '/checkout';
            }
          }
        }, true);
      }
    };
    
    // Setup form listener immediately and also on cart updates
    setupFormListener();
    document.addEventListener('cart:updated', setupFormListener);
    document.addEventListener('cart-updated', setupFormListener);
  }

  async waitForCartOperations() {
    const maxWaitTime = 2000; // Reduced from 5s to 2s
    const checkInterval = 50; // Check every 50ms (faster checks)
    const startTime = Date.now();
    
    while (Date.now() - startTime < maxWaitTime) {
      // Check if any operations are in progress
      const hasPendingOps = 
        this._isAddingGift || 
        this._isRemovingGift || 
        window._freeGiftsGlobalLock.adding.size > 0 ||
        (this._debounceTimeout !== null);
      
      if (!hasPendingOps) {
        // Shorter wait to ensure operations are complete
        await new Promise(resolve => setTimeout(resolve, 100));
        return;
      }
      
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
    
    // If timeout, proceed anyway (cart operations will be blocked by lock)
    console.warn('Timeout waiting for cart operations, proceeding with checkout');
  }

  async validateAndFixCartState() {
    // Fetch latest cart state
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Calculate total excluding free gifts
      const cartTotal = this.calculateCartTotalExcludingFreeGifts(cart);
      
      // Convert thresholds to current currency
      const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
      const t1 = Math.round(this.threshold1 * rate);
      const t2 = Math.round(this.threshold2 * rate);
      const t3 = Math.round(this.threshold3 * rate);
      
      // Check which thresholds are met
      const threshold1Met = t1 > 0 && cartTotal >= t1;
      const threshold2Met = t2 > 0 && cartTotal >= t2;
      const threshold3Met = t3 > 0 && cartTotal >= t3;
      
      // Get free gift items in cart
      const freeGiftItems = cart.items.filter(item => 
        item.properties && item.properties._free_gift === 'true'
      );
      
      // Remove free gifts that shouldn't be there
      const removals = [];
      for (const item of freeGiftItems) {
        const variantId = item.variant_id || item.id;
        let shouldHaveGift = false;
        
        // Check if this variant should be in cart based on thresholds
        if (variantId == this.variant1 && threshold1Met) shouldHaveGift = true;
        if (variantId == this.variant2 && threshold2Met) shouldHaveGift = true;
        if (variantId == this.variant3 && threshold3Met) shouldHaveGift = true;
        
        if (!shouldHaveGift) {
          // Remove this free gift - find line number from current cart
          let lineNumber = null;
          for (let i = 0; i < cart.items.length; i++) {
            const cartItem = cart.items[i];
            if (cartItem.variant_id == variantId || cartItem.id == variantId) {
              lineNumber = i + 1; // Shopify uses 1-based line numbers
              break;
            }
          }
          
          if (lineNumber) {
            removals.push({ line: lineNumber, item: item });
          }
        }
      }
      
      // Remove all invalid free gifts in parallel
      if (removals.length > 0) {
        await Promise.all(removals.map(({ line }) => 
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ line: line, quantity: 0 })
          })
        ));
        console.log('Removed', removals.length, 'invalid free gift(s) before checkout');
      }
      
      // Update internal state
      this.cartTotal = cartTotal;
      this.latestCart = cart;
      
      // Return number of items removed (for conditional second pass)
      return removals.length;
      
    } catch (error) {
      console.error('Error validating cart state:', error);
      throw error;
    }
  }

  // Calculate cart total excluding free gift items (for threshold checking)
  calculateCartTotalExcludingFreeGifts(cart) {
    if (!cart || !cart.items) return 0;
    let total = 0;
    for (const item of cart.items) {
      // Only count items that are NOT free gifts
      const isFreeGift = item.properties && item.properties._free_gift === 'true';
      if (!isFreeGift) {
        total += item.final_line_price || item.line_price || 0;
      }
    }
    return total;
  }

  async fetchUpdatedCartData(skipGiftChecks = false) {
    // Don't process cart updates if checkout is being validated
    if (window._checkoutLock && window._checkoutLock.isLocked) {
      return;
    }
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      // Calculate total excluding free gifts for threshold checking
      this.cartTotal = this.calculateCartTotalExcludingFreeGifts(cart);
      this.latestCart = cart;
      this.updateProgress();
      
      // Skip gift checks if we're in the middle of adding/removing (prevents infinite loops)
      // Also skip if checkout is locked
      if (!skipGiftChecks && !this._isAddingGift && !this._isRemovingGift && 
          (!window._checkoutLock || !window._checkoutLock.isLocked)) {
        // Check both adding and removing free gifts
        await this.maybeRemoveFreeGifts();
        await this.maybeAutoAddGifts();
      }
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
  }

  updateProgress() {
    this.updateProgressText();
    this.updateProductStatuses();
    this.updateProgressBar();
  }

  updateProgressText() {
    const progressText = this.querySelector('[data-js-progress-text]');
    if (!progressText) return;

    // Convert USD thresholds to current currency
    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const convertedThreshold1 = Math.round(this.threshold1 * rate);
    const convertedThreshold2 = Math.round(this.threshold2 * rate);
    const convertedThreshold3 = Math.round(this.threshold3 * rate);

    // Only consider positive thresholds
    const thresholds = [convertedThreshold1, convertedThreshold2, convertedThreshold3].filter(t => t > 0);
    const sortedThresholds = [...thresholds].sort((a, b) => a - b);

    let nextThreshold = null;
    for (let i = 0; i < sortedThresholds.length; i++) {
      if (this.cartTotal < sortedThresholds[i]) {
        nextThreshold = sortedThresholds[i];
        break;
      }
    }

    if (sortedThresholds.length === 0) {
      // No thresholds configured; show nothing specific
      progressText.innerHTML = '';
      return;
    }

    if (nextThreshold === null) {
      progressText.innerHTML = '🎉 All free products unlocked!';
    } else {
      const remaining = nextThreshold - this.cartTotal;
      const remainingFormatted = this.formatMoney(remaining);

      let productName = 'Product';
      if (nextThreshold === convertedThreshold1) {
        productName = this.getProductName(1);
      } else if (nextThreshold === convertedThreshold2) {
        productName = this.getProductName(2);
      } else if (nextThreshold === convertedThreshold3) {
        productName = this.getProductName(3);
      }

      progressText.innerHTML = `You are ${remainingFormatted} away from <span>FREE</span> ${productName}`;
    }
  }

  updateProductStatuses() {
    // Convert USD thresholds to current currency
    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const convertedThreshold1 = Math.round(this.threshold1 * rate);
    const convertedThreshold2 = Math.round(this.threshold2 * rate);
    const convertedThreshold3 = Math.round(this.threshold3 * rate);

    // Only set status for thresholds that are configured (> 0)
    if (convertedThreshold1 > 0) this.setProductStatus(1, this.cartTotal >= convertedThreshold1); 
    if (convertedThreshold2 > 0) this.setProductStatus(2, this.cartTotal >= convertedThreshold2);
    if (convertedThreshold3 > 0) this.setProductStatus(3, this.cartTotal >= convertedThreshold3);
  }

  setProductStatus(productNumber, isUnlocked) {
    const productElement = this.querySelector(`[data-product="${productNumber}"]`);
    const statusElement = this.querySelector(`[data-js-product-status-${productNumber}]`);
    if (!productElement || !statusElement) return;

    if (isUnlocked) {
      productElement.classList.add('unlocked');
      productElement.classList.remove('locked');
      statusElement.innerHTML = '<span class="free-products-progress__check-icon">✅</span>';
    } else {
      productElement.classList.add('locked');
      productElement.classList.remove('unlocked');
    }
  }

  updateProgressBar() {
    const progressFill = this.querySelector('[data-js-progress-fill]');
    if (!progressFill) return;

    // Convert USD thresholds to current currency (same as shipping notice)
    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const convertedThreshold1 = Math.round(this.threshold1 * rate);
    const convertedThreshold2 = Math.round(this.threshold2 * rate);
    const convertedThreshold3 = Math.round(this.threshold3 * rate);

    // Sort thresholds in ascending order
    const thresholds = [convertedThreshold1, convertedThreshold2, convertedThreshold3].filter(t => t > 0);
    const sortedThresholds = [...thresholds].sort((a, b) => a - b);
    
    // Get the bar container to calculate available width
    const barContainer = this.querySelector('[data-js-progress-bar]');
    const availableWidth = barContainer ? barContainer.offsetWidth : 0;
    
    // Start the bar only after first product is unlocked
    if (this.cartTotal < sortedThresholds[0]) {
      // Before first threshold, show 0%
      progressFill.style.width = '0px';
      return;
    }
    
    // Check if we've reached the second product
    const hasReachedSecond = this.cartTotal >= sortedThresholds[1];
    
    // Check which segment we're in
    if (sortedThresholds.length === 1 || this.cartTotal >= sortedThresholds[sortedThresholds.length - 1]) {
      // Only one threshold or reached all thresholds - show 100%
      progressFill.style.width = '100%';
      return;
    }
    
    // Find which segment we're currently in
    for (let i = 0; i < sortedThresholds.length - 1; i++) {
      if (this.cartTotal >= sortedThresholds[i] && this.cartTotal < sortedThresholds[i + 1]) {
        // We're in segment between thresholds[i] and thresholds[i+1]
        const segmentStart = sortedThresholds[i];
        const segmentEnd = sortedThresholds[i + 1];
        const segmentRange = segmentEnd - segmentStart;
        const progressInSegment = this.cartTotal - segmentStart;
        const segmentPercent = (progressInSegment / segmentRange) * 100;
        
        // Calculate overall percent based on which segment we're in
        const basePercent = (i / sortedThresholds.length) * 100;
        const segmentContribution = (1 / sortedThresholds.length) * 100;
        let overallPercent = basePercent + (segmentPercent * segmentContribution / 100);
        
        // Add 85px when second product is reached
        if (hasReachedSecond && availableWidth > 0) {
          const percentWidth = (overallPercent / 100) * availableWidth;
          const finalWidth = percentWidth + 85;
          progressFill.style.width = `${Math.min(finalWidth, availableWidth)}px`;
        } else {
          progressFill.style.width = `${Math.min(overallPercent, 100)}%`;
        }
        return;
      }
    }
    
    // Fallback - should not reach here
    progressFill.style.width = '0%';
  }

  getProductName(productNumber) {
    const productElement = this.querySelector(`[data-product="${productNumber}"] .free-products-progress__product-name`);
    if (!productElement) return `Product ${productNumber}`;
    
    // Get the text content and remove "FREE" label if present
    let productName = productElement.textContent.trim();
    // Remove "FREE " from the beginning (with or without the label span)
    productName = productName.replace(/^FREE\s+/, '');
    
    return productName;
  }

  getVariantId(productNumber) {
    if (productNumber === 1) return this.variant1;
    if (productNumber === 2) return this.variant2;
    if (productNumber === 3) return this.variant3;
    return null;
  }

  isVariantAlreadyInCart(variantId) {
    if (!this.latestCart || !this.latestCart.items || !variantId) return false;
    // Convert to numbers for comparison to handle string/number mismatches
    const variantIdNum = parseInt(variantId);
    return this.latestCart.items.some(item => {
      const itemVariantId = parseInt(item.variant_id);
      return itemVariantId === variantIdNum;
    });
  }

  // Count how many times a variant appears in cart (for free gifts)
  getVariantQuantityInCart(variantId) {
    if (!this.latestCart || !this.latestCart.items || !variantId) return 0;
    const variantIdNum = parseInt(variantId);
    return this.latestCart.items.filter(item => {
      const itemVariantId = parseInt(item.variant_id);
      return itemVariantId === variantIdNum;
    }).reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  // Get free gift items from cart (items with _free_gift property)
  getFreeGiftItemsInCart() {
    if (!this.latestCart || !this.latestCart.items) return [];
    return this.latestCart.items.filter(item => 
      item.properties && 
      item.properties._free_gift === 'true'
    );
  }

  // Find which line number a variant is at in the cart
  findLineNumberForVariant(variantId) {
    if (!this.latestCart || !this.latestCart.items) return null;
    for (let i = 0; i < this.latestCart.items.length; i++) {
      const item = this.latestCart.items[i];
      if (item.variant_id === variantId || item.id === variantId) {
        return i + 1; // Shopify uses 1-based line numbers
      }
    }
    return null;
  }

  async maybeRemoveFreeGifts() {
    if (this._isRemovingGift || this._isAddingGift) return;
    // Don't process if checkout is locked
    if (window._checkoutLock && window._checkoutLock.isLocked) return;

    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const t1 = Math.round(this.threshold1 * rate);
    const t2 = Math.round(this.threshold2 * rate);
    const t3 = Math.round(this.threshold3 * rate);

    // Check which thresholds are NOT met
    const checks = [
      { num: 1, threshold: t1, unlocked: t1 > 0 && this.cartTotal >= t1, variantId: this.getVariantId(1) },
      { num: 2, threshold: t2, unlocked: t2 > 0 && this.cartTotal >= t2, variantId: this.getVariantId(2) },
      { num: 3, threshold: t3, unlocked: t3 > 0 && this.cartTotal >= t3, variantId: this.getVariantId(3) }
    ];

    // Get all free gift items currently in cart
    const freeGiftItems = this.getFreeGiftItemsInCart();

    // For each free gift in cart, check if its threshold is still met
    for (const item of freeGiftItems) {
      const variantId = item.variant_id || item.id;
      
      // Find which threshold this variant belongs to
      const thresholdCheck = checks.find(c => c.variantId === variantId);
      
      // If we found the threshold and it's NOT unlocked, remove this item
      if (thresholdCheck && !thresholdCheck.unlocked) {
        await this.removeFreeGiftVariant(item);
        // Remove from tracking set
        this._addedGiftVariantIds.delete(variantId);
      }
      // Also remove if variant ID doesn't match any configured gift (shouldn't happen, but safety check)
      else if (!thresholdCheck) {
        // This free gift doesn't match any configured variant, but we'll leave it alone
        // as it might be from a different system or manually added
      }
    }
  }

  async removeFreeGiftVariant(cartItem) {
    try {
      this._isRemovingGift = true;
      
      // Find line number for this item
      const lineNumber = this.findLineNumberForVariant(cartItem.variant_id || cartItem.id);
      
      if (!lineNumber) {
        console.warn('Could not find line number for free gift variant:', cartItem.variant_id);
        return;
      }

      // Remove item by setting quantity to 0
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: lineNumber, quantity: 0 })
      });

      console.log('Removed free gift from cart:', cartItem.title);

      // Refresh cart UI if helper is present; otherwise, emit events
      if (window.refreshCart && typeof window.refreshCart === 'function') {
        window.refreshCart();
      } else {
        document.dispatchEvent(new Event('cart:updated'));
        document.dispatchEvent(new Event('cart-updated'));
      }
      
      // Fetch updated cart to keep internal state in sync (skip gift checks to prevent loops)
      // The cart update events will trigger another fetchUpdatedCartData which will check gifts
      await this.fetchUpdatedCartData(true);
    } catch (e) {
      console.error('Failed to remove free gift variant', e);
    } finally {
      this._isRemovingGift = false;
    }
  }

  async maybeAutoAddGifts() {
    if (this._isAddingGift || this._isRemovingGift) return;
    // Don't process if checkout is locked
    if (window._checkoutLock && window._checkoutLock.isLocked) return;

    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const t1 = Math.round(this.threshold1 * rate);
    const t2 = Math.round(this.threshold2 * rate);
    const t3 = Math.round(this.threshold3 * rate);

    const checks = [
      { num: 1, unlocked: t1 > 0 && this.cartTotal >= t1, variantId: this.getVariantId(1) },
      { num: 2, unlocked: t2 > 0 && this.cartTotal >= t2, variantId: this.getVariantId(2) },
      { num: 3, unlocked: t3 > 0 && this.cartTotal >= t3, variantId: this.getVariantId(3) }
    ];

    // Sync tracking set with actual cart state FIRST
    // Reset and rebuild from actual cart
    this._addedGiftVariantIds.clear();
    for (const c of checks) {
      if (!c.variantId) continue;
      // If variant is in cart (especially as free gift), track it
      if (this.isVariantAlreadyInCart(c.variantId)) {
        this._addedGiftVariantIds.add(c.variantId);
      }
    }

    // Now check what needs to be added (only variants not in cart yet)
    for (const c of checks) {
      if (!c.variantId || !c.unlocked) continue;
      
      const variantIdStr = String(c.variantId);
      
      // GLOBAL CHECK: Is any instance currently adding this?
      if (window._freeGiftsGlobalLock.adding.has(variantIdStr)) {
        console.log('Variant being added by another instance, skipping:', c.variantId);
        continue;
      }
      
      // Check actual cart quantity - if already in cart, skip
      const quantity = this.getVariantQuantityInCart(c.variantId);
      if (quantity > 0) {
        this._addedGiftVariantIds.add(c.variantId);
        continue;
      }
      
      // Double check tracking set
      if (this._addedGiftVariantIds.has(c.variantId)) {
        continue;
      }
      
      // Only add if NOT in cart at all
      if (!this.isVariantAlreadyInCart(c.variantId)) {
        // Mark as adding BEFORE the async call to prevent duplicates
        this._addedGiftVariantIds.add(c.variantId);
        await this.addFreeGiftVariant(c.variantId);
      }
    }
  }

  async addFreeGiftVariant(variantId) {
    const variantIdStr = String(variantId);
    
    // Don't add gifts if checkout is locked
    if (window._checkoutLock && window._checkoutLock.isLocked) {
      console.log('Checkout is locked, skipping free gift add:', variantId);
      return;
    }
    
    // GLOBAL LOCK: Check if any instance is already adding this variant
    if (window._freeGiftsGlobalLock.adding.has(variantIdStr)) {
      console.log('Another instance is already adding variant:', variantId);
      return;
    }
    
    // Check if pending with recent timestamp
    const pendingTimestamp = window._freeGiftsGlobalLock.pending.get(variantIdStr);
    if (pendingTimestamp && (Date.now() - pendingTimestamp) < 3000) {
      console.log('Variant add still pending from recent operation:', variantId);
      return;
    }
    
    try {
      this._isAddingGift = true;
      
      // Mark globally as adding
      window._freeGiftsGlobalLock.adding.add(variantIdStr);
      window._freeGiftsGlobalLock.pending.set(variantIdStr, Date.now());
      
      // Final check - if already in cart, abort
      const quantity = this.getVariantQuantityInCart(variantId);
      if (quantity > 0) {
        console.log('Free gift variant already in cart (qty:', quantity, '), skipping:', variantId);
        // Clean up locks
        window._freeGiftsGlobalLock.adding.delete(variantIdStr);
        window._freeGiftsGlobalLock.pending.delete(variantIdStr);
        return;
      }
      
      console.log('Adding free gift variant:', variantId);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties: { _free_gift: 'true' } })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      const result = await response.json();
      console.log('Successfully added free gift variant:', variantId, result);
      
      // Wait a moment for cart to update on server
      await new Promise(resolve => setTimeout(resolve, 200));

      // Refresh cart UI WITHOUT dispatching events (to prevent loops)
      if (window.refreshCart && typeof window.refreshCart === 'function') {
        window.refreshCart();
      }
      
      // Fetch updated cart but skip gift checks (events will trigger another check)
      await this.fetchUpdatedCartData(true);
      
      // Keep lock for a bit longer to prevent rapid re-adds
      setTimeout(() => {
        window._freeGiftsGlobalLock.adding.delete(variantIdStr);
        window._freeGiftsGlobalLock.pending.delete(variantIdStr);
      }, 1000);
    } catch (e) {
      console.error('Failed to add free gift variant', e);
      // Remove from tracking if add failed so it can be retried
      this._addedGiftVariantIds.delete(variantId);
      // Clean up locks on error
      window._freeGiftsGlobalLock.adding.delete(variantIdStr);
      window._freeGiftsGlobalLock.pending.delete(variantIdStr);
    } finally {
      this._isAddingGift = false;
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return `${this.currencySymbol}${amount.toFixed(2)}`;
  }
}

// Global lock to prevent duplicate adds across all instances
window._freeGiftsGlobalLock = window._freeGiftsGlobalLock || {
  adding: new Set(),
  pending: new Map(), // variantId -> timestamp
  timeout: 5000 // 5 second timeout for pending operations
};

// Clean up stale pending entries
setInterval(() => {
  const now = Date.now();
  for (const [variantId, timestamp] of window._freeGiftsGlobalLock.pending.entries()) {
    if (now - timestamp > window._freeGiftsGlobalLock.timeout) {
      window._freeGiftsGlobalLock.pending.delete(variantId);
      window._freeGiftsGlobalLock.adding.delete(variantId);
    }
  }
}, 1000);

// Register the custom element
customElements.define('free-products-progress', FreeProductsProgress);

// Also register as a regular class for manual initialization
window.FreeProductsProgress = FreeProductsProgress;

// Fallback: manually initialize if custom element doesn't work
document.addEventListener('DOMContentLoaded', function() {
  const progressElements = document.querySelectorAll('free-products-progress');
  if (progressElements.length === 0) {
    console.log('No free-products-progress elements found!');
  }
});